import type { ProjectApiResponse } from '../../features/projects/projects.models';

/**
 * プロジェクト詳細Mockデータ
 * APIレスポンス形式（ProjectApiResponse）で定義
 *
 * Issue #475 Phase 5.5 / Issue #550: `extractionFailedReason` は
 * `extractionStatus='failed'` のときだけ値を持つ。project-007〜009 / project-015 に
 * 4 状態（usage_limit_exceeded / ai_server_error / null = 理由不明 / conversion_error）を
 * 用意し、総括表バナーの文言の出し分けを画面で確認できるようにしている。
 * 抽出ソース側（extraction-sources.mock の ocrFailedReason）との対応は
 * mocks/mock-data/ocr-failed-reason.spec.ts で固定している。
 */
export const MOCK_PROJECT_DETAILS: Record<string, ProjectApiResponse> = {
  'project-001': {
    publicId: 'project-001',
    contractTypeId: 'ctp_001',
    name: '直接雇用・正社員',
    aiProjectId: 'proj_001',
    aiProcessStatus: 'completed',
    aiProcessStartedAt: '2025-01-10T08:00:00.000Z',
    aiProcessCompletedAt: '2025-01-10T09:00:00.000Z',
    aiProcessError: null,
    caseCount: 190,
    completedCaseCount: 190,
    incompleteCaseCount: 0,
    aiReviewedCaseCount: 190,
    hasExtractionSource: true,
    extractionStatus: 'completed',
    latestExtractionRunPublicId: 'run_mock123',
    extractionFailedReason: null,
    projectPeriod: {
      start: { year: 2025, month: 4 },
      end: { year: 2026, month: 3 },
    },
    updatedByUser: { publicId: 'user-001', email: 'user@example.com' },
    mark: null,
    memo: '',
    createdAt: '2025-01-01T00:00:00.000Z',
    updatedAt: '2025-01-15T10:00:00.000Z',
  },
  'project-002': {
    publicId: 'project-002',
    contractTypeId: 'ctp_002',
    name: '直接雇用・契約社員（有期）',
    aiProjectId: 'proj_002',
    aiProcessStatus: 'completed',
    aiProcessStartedAt: '2025-01-10T08:00:00.000Z',
    aiProcessCompletedAt: '2025-01-10T10:00:00.000Z',
    aiProcessError: null,
    caseCount: 58,
    completedCaseCount: 30,
    incompleteCaseCount: 28,
    aiReviewedCaseCount: 20,
    hasExtractionSource: true,
    extractionStatus: 'completed',
    latestExtractionRunPublicId: 'run_mock456',
    extractionFailedReason: null,
    projectPeriod: {
      start: { year: 2024, month: 4 },
      end: { year: 2025, month: 3 },
    },
    updatedByUser: { publicId: 'user-001', email: 'user@example.com' },
    mark: null,
    memo: '',
    createdAt: '2025-01-01T00:00:00.000Z',
    updatedAt: '2025-01-14T15:30:00.000Z',
  },
  'project-003': {
    publicId: 'project-003',
    contractTypeId: 'ctp_003',
    name: '直接雇用・アルバイト',
    aiProjectId: 'proj_003',
    aiProcessStatus: 'completed',
    aiProcessStartedAt: '2025-01-09T08:00:00.000Z',
    aiProcessCompletedAt: '2025-01-09T09:00:00.000Z',
    aiProcessError: null,
    caseCount: 45,
    completedCaseCount: 45,
    incompleteCaseCount: 0,
    aiReviewedCaseCount: 45,
    hasExtractionSource: true,
    extractionStatus: 'completed',
    latestExtractionRunPublicId: 'run_mock789',
    extractionFailedReason: null,
    projectPeriod: {
      start: { year: 2025, month: 4 },
      end: { year: 2026, month: 3 },
    },
    updatedByUser: { publicId: 'user-001', email: 'user@example.com' },
    mark: null,
    memo: '',
    createdAt: '2025-01-01T00:00:00.000Z',
    updatedAt: '2025-01-13T09:00:00.000Z',
  },
  'project-004': {
    publicId: 'project-004',
    contractTypeId: 'ctp_004',
    name: '間接雇用・出向者',
    aiProjectId: null,
    aiProcessStatus: null,
    aiProcessStartedAt: null,
    aiProcessCompletedAt: null,
    aiProcessError: null,
    caseCount: 20,
    completedCaseCount: 10,
    incompleteCaseCount: 10,
    aiReviewedCaseCount: 0,
    hasExtractionSource: false,
    extractionStatus: 'idle',
    latestExtractionRunPublicId: null,
    extractionFailedReason: null,
    projectPeriod: {
      start: { year: 2025, month: 4 },
      end: { year: 2026, month: 3 },
    },
    updatedByUser: { publicId: 'user-002', email: 'admin@example.com' },
    mark: null,
    memo: '',
    createdAt: '2025-01-01T00:00:00.000Z',
    updatedAt: '2025-01-12T11:00:00.000Z',
  },
  'project-005': {
    publicId: 'project-005',
    contractTypeId: 'ctp_005',
    name: '直接雇用・正社員',
    aiProjectId: 'proj_005',
    aiProcessStatus: 'processing',
    aiProcessStartedAt: '2025-01-14T08:00:00.000Z',
    aiProcessCompletedAt: null,
    aiProcessError: null,
    caseCount: 200,
    completedCaseCount: 50,
    incompleteCaseCount: 150,
    aiReviewedCaseCount: 50,
    hasExtractionSource: true,
    extractionStatus: 'processing',
    latestExtractionRunPublicId: null,
    extractionFailedReason: null,
    projectPeriod: {
      start: { year: 2024, month: 4 },
      end: { year: 2025, month: 3 },
    },
    updatedByUser: { publicId: 'user-001', email: 'user@example.com' },
    mark: null,
    memo: '',
    createdAt: '2025-01-01T00:00:00.000Z',
    updatedAt: '2025-01-14T09:30:00.000Z',
  },
  'project-006': {
    publicId: 'project-006',
    contractTypeId: 'ctp_006',
    name: '直接雇用・契約社員（無期）',
    aiProjectId: null,
    aiProcessStatus: null,
    aiProcessStartedAt: null,
    aiProcessCompletedAt: null,
    aiProcessError: null,
    caseCount: 35,
    completedCaseCount: 0,
    incompleteCaseCount: 35,
    aiReviewedCaseCount: 0,
    hasExtractionSource: false,
    extractionStatus: 'idle',
    latestExtractionRunPublicId: null,
    extractionFailedReason: null,
    projectPeriod: {
      start: { year: 2025, month: 4 },
      end: { year: 2026, month: 3 },
    },
    updatedByUser: null,
    mark: null,
    memo: '',
    createdAt: '2025-01-01T00:00:00.000Z',
    updatedAt: '2025-01-10T14:00:00.000Z',
  },
  'project-007': {
    publicId: 'project-007',
    contractTypeId: 'ctp_001',
    name: '直接雇用・正社員',
    aiProjectId: null,
    aiProcessStatus: null,
    aiProcessStartedAt: null,
    aiProcessCompletedAt: null,
    aiProcessError: null,
    caseCount: 50,
    completedCaseCount: 0,
    incompleteCaseCount: 50,
    aiReviewedCaseCount: 0,
    hasExtractionSource: false,
    // Issue #475 Phase 5.5: 402（利用上限）でファイル転送が拒否された場合も
    // Project は理由付きで ExtractionFailed へ遷移する
    // （ADR-20260825_2000 D8 の「Project を動かさない」を Phase 5.5 で改めた。
    //   viewer のポーリングが idle のままでは終端しないため）
    extractionStatus: 'failed',
    latestExtractionRunPublicId: null,
    extractionFailedReason: 'usage_limit_exceeded',
    projectPeriod: {
      start: { year: 2024, month: 4 },
      end: { year: 2025, month: 3 },
    },
    updatedByUser: null,
    mark: null,
    memo: '',
    createdAt: '2025-02-01T00:00:00.000Z',
    updatedAt: '2025-02-01T10:00:00.000Z',
  },
  'project-008': {
    publicId: 'project-008',
    contractTypeId: 'ctp_002',
    name: '直接雇用・契約社員（有期）',
    aiProjectId: null,
    aiProcessStatus: null,
    aiProcessStartedAt: null,
    aiProcessCompletedAt: null,
    aiProcessError: null,
    caseCount: 30,
    completedCaseCount: 0,
    incompleteCaseCount: 30,
    aiReviewedCaseCount: 0,
    hasExtractionSource: false,
    // Issue #475: ai_server_error 由来の OCR 失敗は webhook 経由で
    // Project も Created|Extracting → ExtractionFailed に遷移する
    extractionStatus: 'failed',
    latestExtractionRunPublicId: null,
    extractionFailedReason: 'ai_server_error',
    projectPeriod: {
      start: { year: 2025, month: 4 },
      end: { year: 2026, month: 3 },
    },
    updatedByUser: null,
    mark: null,
    memo: '',
    createdAt: '2025-02-01T00:00:00.000Z',
    updatedAt: '2025-02-01T10:00:00.000Z',
  },
  'project-009': {
    publicId: 'project-009',
    contractTypeId: 'ctp_003',
    name: '直接雇用・アルバイト',
    aiProjectId: null,
    aiProcessStatus: null,
    aiProcessStartedAt: null,
    aiProcessCompletedAt: null,
    aiProcessError: null,
    caseCount: 25,
    completedCaseCount: 0,
    incompleteCaseCount: 25,
    aiReviewedCaseCount: 0,
    hasExtractionSource: false,
    // Issue #475: Phase 3 以前の OCR 失敗（理由不明）も webhook 経由なので
    // Project は ExtractionFailed に遷移している。
    // Phase 5.5 以前に失敗した既存行は理由を遡って埋められないため理由は null（理由不明）
    extractionStatus: 'failed',
    latestExtractionRunPublicId: null,
    extractionFailedReason: null,
    projectPeriod: {
      start: { year: 2024, month: 4 },
      end: { year: 2025, month: 3 },
    },
    updatedByUser: null,
    mark: null,
    memo: '',
    createdAt: '2025-02-01T00:00:00.000Z',
    updatedAt: '2025-02-01T10:00:00.000Z',
  },
  'project-010': {
    publicId: 'project-010',
    contractTypeId: 'ctp_001',
    name: '直接雇用・正社員',
    aiProjectId: null,
    aiProcessStatus: null,
    aiProcessStartedAt: null,
    aiProcessCompletedAt: null,
    aiProcessError: null,
    caseCount: 80,
    completedCaseCount: 0,
    incompleteCaseCount: 80,
    aiReviewedCaseCount: 0,
    hasExtractionSource: false,
    extractionStatus: 'idle',
    latestExtractionRunPublicId: null,
    extractionFailedReason: null,
    projectPeriod: {
      start: { year: 2025, month: 4 },
      end: { year: 2026, month: 3 },
    },
    updatedByUser: null,
    mark: null,
    memo: '',
    createdAt: '2025-01-01T00:00:00.000Z',
    updatedAt: '2025-01-13T10:00:00.000Z',
  },
  'project-011': {
    publicId: 'project-011',
    contractTypeId: 'ctp_002',
    name: '直接雇用・契約社員（有期）',
    aiProjectId: null,
    aiProcessStatus: null,
    aiProcessStartedAt: null,
    aiProcessCompletedAt: null,
    aiProcessError: null,
    caseCount: 40,
    completedCaseCount: 0,
    incompleteCaseCount: 40,
    aiReviewedCaseCount: 0,
    hasExtractionSource: false,
    extractionStatus: 'idle',
    latestExtractionRunPublicId: null,
    extractionFailedReason: null,
    projectPeriod: {
      start: { year: 2025, month: 4 },
      end: { year: 2026, month: 3 },
    },
    updatedByUser: null,
    mark: null,
    memo: '',
    createdAt: '2025-01-01T00:00:00.000Z',
    updatedAt: '2025-01-12T10:00:00.000Z',
  },
  // idle / failed / cancelled の各 aiProcessStatus を持つエントリ（スタイル確認用）
  'project-012': {
    publicId: 'project-012',
    contractTypeId: 'ctp_003',
    name: '間接雇用・派遣社員（idle確認用）',
    aiProjectId: 'proj_012',
    aiProcessStatus: 'idle',
    aiProcessStartedAt: null,
    aiProcessCompletedAt: null,
    aiProcessError: null,
    caseCount: 15,
    completedCaseCount: 0,
    incompleteCaseCount: 15,
    aiReviewedCaseCount: 0,
    hasExtractionSource: true,
    extractionStatus: 'idle',
    latestExtractionRunPublicId: null,
    extractionFailedReason: null,
    projectPeriod: {
      start: { year: 2025, month: 4 },
      end: { year: 2026, month: 3 },
    },
    updatedByUser: { publicId: 'user-001', email: 'user@example.com' },
    mark: null,
    memo: '',
    createdAt: '2025-03-01T00:00:00.000Z',
    updatedAt: '2025-03-01T10:00:00.000Z',
  },
  'project-013': {
    publicId: 'project-013',
    contractTypeId: 'ctp_004',
    name: '間接雇用・業務委託（failed確認用）',
    aiProjectId: 'proj_013',
    aiProcessStatus: 'failed',
    aiProcessStartedAt: '2025-03-10T08:00:00.000Z',
    aiProcessCompletedAt: null,
    aiProcessError: { code: 'TIMEOUT', message: '処理タイムアウト' },
    caseCount: 10,
    completedCaseCount: 0,
    incompleteCaseCount: 10,
    aiReviewedCaseCount: 0,
    hasExtractionSource: true,
    extractionStatus: 'failed',
    latestExtractionRunPublicId: null,
    extractionFailedReason: null,
    projectPeriod: {
      start: { year: 2025, month: 4 },
      end: { year: 2026, month: 3 },
    },
    updatedByUser: { publicId: 'user-001', email: 'user@example.com' },
    mark: null,
    memo: '',
    createdAt: '2025-03-01T00:00:00.000Z',
    updatedAt: '2025-03-10T12:00:00.000Z',
  },
  'project-014': {
    publicId: 'project-014',
    contractTypeId: 'ctp_005',
    name: '直接雇用・嘱託（cancelled確認用）',
    aiProjectId: 'proj_014',
    aiProcessStatus: 'cancelled',
    aiProcessStartedAt: '2025-03-15T08:00:00.000Z',
    aiProcessCompletedAt: null,
    aiProcessError: null,
    caseCount: 5,
    completedCaseCount: 0,
    incompleteCaseCount: 5,
    aiReviewedCaseCount: 0,
    hasExtractionSource: true,
    extractionStatus: 'idle',
    latestExtractionRunPublicId: null,
    extractionFailedReason: null,
    projectPeriod: {
      start: { year: 2025, month: 4 },
      end: { year: 2026, month: 3 },
    },
    updatedByUser: { publicId: 'user-002', email: 'admin@example.com' },
    mark: null,
    memo: '',
    createdAt: '2025-03-01T00:00:00.000Z',
    updatedAt: '2025-03-15T09:00:00.000Z',
  },
  'project-015': {
    publicId: 'project-015',
    contractTypeId: 'ctp_001',
    name: '直接雇用・正社員（変換エラー確認用）',
    aiProjectId: null,
    aiProcessStatus: null,
    aiProcessStartedAt: null,
    aiProcessCompletedAt: null,
    aiProcessError: null,
    caseCount: 0,
    completedCaseCount: 0,
    incompleteCaseCount: 0,
    aiReviewedCaseCount: 0,
    hasExtractionSource: true,
    // Issue #550: 総括表の PDF 変換に失敗すると ConvertToPreviewFailed webhook 経由で
    // OcrFailed（conversion_error）になり、Project も ExtractionFailed に遷移する。
    // 修正前はこの webhook を skip していたため OcrProcessing のまま固まっていた
    extractionStatus: 'failed',
    latestExtractionRunPublicId: null,
    extractionFailedReason: 'conversion_error',
    projectPeriod: {
      start: { year: 2025, month: 4 },
      end: { year: 2026, month: 3 },
    },
    updatedByUser: null,
    mark: null,
    memo: '',
    createdAt: '2026-09-01T00:00:00.000Z',
    updatedAt: '2026-09-01T02:05:00.000Z',
  },
};

/**
 * プロジェクトIDからプロジェクト詳細を取得
 */
export function findProjectById(
  projectId: string,
): ProjectApiResponse | undefined {
  return MOCK_PROJECT_DETAILS[projectId];
}
